import React from "react";
import { Link } from "react-router-dom";
import "./SeoLandingPage.css";

const landingPages = {
  kanzi: {
    eyebrow: "Kanzi the bonobo",
    title: "Kanzi changed what science could imagine about language.",
    lead:
      "Kanzi (1980-2025) was a bonobo whose comprehension of spoken English, use of lexigrams, cultural learning, and relationships with humans made him one of the most important figures in ape language research.",
    image: "/KanziHeadshot.png",
    imageAlt: "Kanzi the bonobo looking to the right",
    sections: [
      {
        heading: "Who was Kanzi?",
        text:
          "Kanzi was born in 1980 and became known internationally for understanding spoken English and communicating with humans through lexigrams: abstract symbols arranged on a keyboard. His abilities were not trained through simple command-response drills. They grew inside a social, language-rich environment shaped by long-term relationships.",
      },
      {
        heading: "Why Kanzi matters",
        text:
          "Kanzi's life challenged assumptions about language, culture, learning, and the human mind. He learned to use symbols, understand narratives, make tools, respond to spoken requests, participate in shared activities, and reveal forms of attention and intention that demanded serious scientific consideration.",
      },
      {
        heading: "The work now",
        text:
          "Bonobo Hope Initiative is working to restore the research trajectory that made Kanzi's communication possible: sustained access, trust, cultural continuity, lexigram tools, careful documentation, and a serious commitment to bonobos as communicative beings.",
      },
    ],
    links: [
      { to: "/kanzi", label: "Celebrate Kanzi" },
      { to: "/keyboard", label: "Try the lexigram keyboard" },
      { to: "/SueBiblio", label: "Read the research" },
    ],
  },
  bonobos: {
    eyebrow: "Bonobos",
    title: "Bonobos are intelligent, social, emotionally complex great apes.",
    lead:
      "Bonobos (Pan paniscus) are great apes closely related to humans. Their social lives, communication, empathy, play, and cultural learning make them central to questions about language, mind, and what humans owe other beings.",
    image: "/KanziSlideshow/GroupPhoto.jpg",
    imageAlt: "A group of bonobos",
    sections: [
      {
        heading: "What makes bonobos distinct?",
        text:
          "Bonobos are known for complex social relationships, rich emotional expression, cooperative behavior, and sensitivity to social context. Their intelligence is not abstract or mechanical; it is lived through relationships, play, care, conflict resolution, and shared attention.",
      },
      {
        heading: "Bonobos and language research",
        text:
          "Kanzi, Panbanisha, Nyota, Teco, and other bonobos have shown why bonobos matter to the study of language and culture. Lexigram research asks what becomes possible when apes are given symbols, time, trust, and a stable human-bonobo cultural environment.",
      },
      {
        heading: "Conservation and dignity",
        text:
          "Bonobos are endangered, and their survival depends on habitat protection, conservation, public education, and ethical care. BHI's work connects conservation with the deeper scientific and moral question of how communicative beings should be treated.",
      },
    ],
    links: [
      { to: "/bonobo-language-research", label: "Explore language research" },
      { to: "/video", label: "Watch bonobo videos" },
      { to: "/donate", label: "Support BHI" },
    ],
  },
  "bonobo-language-research": {
    eyebrow: "Bonobo language research",
    title: "Ape language research is a question about culture, not tricks.",
    lead:
      "Bonobo and chimpanzee language research investigates how symbolic communication can develop when apes live in language-rich environments with stable relationships, meaningful routines, and access to lexigram keyboards.",
    image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    imageAlt: "Kanzi pointing toward a lexigram",
    sections: [
      {
        heading: "The original research trajectory",
        text:
          "The work associated with Kanzi and Dr. Sue Savage-Rumbaugh explored spoken English comprehension, lexigram use, cultural learning, intentional communication, and the conditions under which apes could participate in symbolic environments.",
      },
      {
        heading: "Why restoration matters",
        text:
          "The goal is not simply to preserve a historical legacy. BHI seeks to revive and restore the conditions that allowed bonobos and chimpanzees to communicate meaningfully with humans: access, continuity, enrichment, scientific attention, and dignity.",
      },
      {
        heading: "Evidence and education",
        text:
          "Videos, publications, lexigram resources, and archival material help the public understand why this research matters. The work sits at the intersection of psychology, anthropology, linguistics, philosophy, conservation, and ethics.",
      },
    ],
    links: [
      { to: "/SueBiblio", label: "View bibliography" },
      { to: "/keyboard", label: "Use the keyboard" },
      { to: "/video", label: "Watch evidence" },
    ],
  },
  lexigrams: {
    eyebrow: "Lexigrams",
    title: "Lexigrams gave Kanzi a symbolic doorway into shared language.",
    lead:
      "Lexigrams are abstract symbols used in ape language research. For Kanzi and other bonobos, lexigram keyboards created a shared symbolic space where objects, actions, requests, places, and ideas could be represented.",
    image: "/KeyboardP1_6.png",
    imageAlt: "A lexigram keyboard",
    sections: [
      {
        heading: "What are lexigrams?",
        text:
          "A lexigram is not a picture of an object. It is an arbitrary symbol whose meaning is learned through use. That arbitrariness matters because it allows researchers to study symbolic understanding rather than simple visual matching.",
      },
      {
        heading: "How Kanzi used lexigrams",
        text:
          "Kanzi used lexigrams to communicate with human partners and to participate in a broader symbolic environment. His use of the keyboard was connected to spoken English comprehension, shared routines, play, requests, and social interaction.",
      },
      {
        heading: "Try the keyboard",
        text:
          "The Bonobo Hope Keyboard lets visitors explore lexigrams and hear words spoken aloud. It is a public learning tool for understanding the symbolic system that helped shape Kanzi's communicative world.",
      },
    ],
    links: [
      { to: "/keyboard", label: "Open the keyboard" },
      { to: "/kanzi", label: "Learn about Kanzi" },
      { to: "/bonobo-language-research", label: "Research context" },
    ],
  },
};

const SeoLandingPage = ({ page }) => {
  const content = landingPages[page];

  if (!content) return null;

  return (
    <main className="seo-page">
      <section className="seo-hero" aria-labelledby={`${page}-title`}>
        <div>
          <p className="seo-eyebrow">{content.eyebrow}</p>
          <h1 id={`${page}-title`}>{content.title}</h1>
          <p>{content.lead}</p>
          <div className="seo-actions">
            {content.links.map((link) => (
              <Link to={link.to} key={link.to}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
        <img src={content.image} alt={content.imageAlt} />
      </section>

      <section className="seo-continuum" aria-label={`${content.eyebrow} details`}>
        <div className="seo-continuum__inner">
          {content.sections.map((section) => (
            <article className="seo-section" key={section.heading}>
              <h2>{section.heading}</h2>
              <p>{section.text}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default SeoLandingPage;
